import { create } from 'zustand'
import type { LinkManifest } from '../types'
import {
  ApiError,
  fetchManifest,
  setCapabilityRefreshHandler,
  setClientPolicySnapshot,
  type ExpiredContact,
} from '../api/client'

// `passcode` = the link is gated and we need the viewer to enter the gallery
// passcode before we can render anything. `wrongPin` flags a rejected attempt so
// the screen can show an inline error without leaving the passcode state.
type Status = 'idle' | 'loading' | 'ready' | 'error' | 'passcode' | 'access'

interface ManifestState {
  status: Status
  shareToken: string | null
  manifest: LinkManifest | null
  error: string | null
  /** True after a pin attempt that the worker rejected (drives the inline error). */
  wrongPin: boolean
  /**
   * The photographer's name + contact handle from a 410 (expired/revoked) error
   * body. Lets the error screen tell a returning client who to reach out to,
   * instead of leaving them at a static dead-end.
   */
  expiredContact: ExpiredContact | null
  load: (shareToken: string, pin?: string) => Promise<void>
  /** Submit the viewer-entered passcode and re-load. */
  submitPin: (pin: string) => Promise<void>
  clear: () => void
}

export const useManifest = create<ManifestState>((set, get) => ({
  status: 'idle',
  shareToken: null,
  manifest: null,
  error: null,
  wrongPin: false,
  expiredContact: null,
  load: async (shareToken: string, pin?: string) => {
    if (get().status === 'loading' && get().shareToken === shareToken) return
    // Loading is a deny-by-default transition. An action rejected because the
    // policy changed must not race a second click using the old grant.
    setClientPolicySnapshot(shareToken, null)
    set({ status: 'loading', shareToken, error: null })
    try {
      const { manifest } = await fetchManifest(shareToken, pin)
      set({ status: 'ready', manifest, error: null, wrongPin: false, expiredContact: null })
    } catch (err) {
      // A passcode-gated link (or a wrong pin) routes to the dedicated passcode
      // screen instead of the generic error screen. The worker answers a missing
      // pin with `passcode_required` and a wrong one with `passcode_incorrect`.
      if (
        err instanceof ApiError &&
        (err.code === 'passcode_required' || err.code === 'passcode_incorrect')
      ) {
        set({
          status: 'passcode',
          manifest: null,
          error: null,
          wrongPin: err.code === 'passcode_incorrect' || Boolean(pin),
        })
        return
      }
      // A restricted link: holding the URL is not enough, so route to the
      // access gate rather than the generic "something went wrong" screen —
      // being told your invitation is broken when it is not is worse than
      // being asked to confirm who you are.
      if (
        err instanceof ApiError &&
        (err.code === 'access_verification_required' || err.code === 'wrong_account')
      ) {
        set({ status: 'access', manifest: null, error: null })
        return
      }
      set({
        status: 'error',
        manifest: null,
        error: err instanceof Error ? err.message : 'Unknown error',
        // A 410 carries the studio contact so the expired screen isn't a dead end.
        expiredContact: err instanceof ApiError ? (err.photographer ?? null) : null,
      })
    }
  },
  submitPin: async (pin: string) => {
    const token = get().shareToken
    if (!token) return
    await get().load(token, pin)
  },
  clear: () => {
    const token = get().shareToken
    if (token) setClientPolicySnapshot(token, null)
    set({
      status: 'idle',
      shareToken: null,
      manifest: null,
      error: null,
      wrongPin: false,
      expiredContact: null,
    })
  },
}))

// A rejected write with `policy_changed` / `session_required` enters the same
// authoritative load path as a navigation. On success the new DTO + voter JWT
// replace the stale pair; on failure the old manifest is removed.
setCapabilityRefreshHandler((shareToken) => useManifest.getState().load(shareToken))

/**
 * Read the share token from the URL. We support both:
 *   /#/?t=k7f29pq
 *   /#/?token=k7f29pq
 * The outer query is also accepted. No previously visited gallery is inferred
 * when the URL has no token; gallery navigation must retain its own token.
 */
export function readShareTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  const qs = hash.includes('?')
    ? hash.slice(hash.indexOf('?') + 1)
    : window.location.search.slice(1)
  const params = new URLSearchParams(qs)
  return params.get('t') ?? params.get('token')
}

/**
 * Read a sneak-peek token (`#/?p=<slug>`) — the view-only mini-gallery a
 * studio sends within hours of the shoot. Separate URL slug with its own
 * revoke, no picking and no downloads; before this route existed the minted
 * sneakPeekUrl dropped the family on the studio sign-in portal.
 */
export function readSneakTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  const qs = hash.includes('?')
    ? hash.slice(hash.indexOf('?') + 1)
    : window.location.search.slice(1)
  const params = new URLSearchParams(qs)
  return params.get('p') ?? params.get('peek')
}
